// src/pages/parts/UserList.js
import React, { useState, useEffect, useCallback } from 'react';
import EditUserForm from '../widgets/EditUserForm'; 
import { apiService } from '../services/api'; 
import '../style/UserList.css'; 

const ITEMS_PER_PAGE = 15;

const UserList = ({ users, organisations, isLoading, onRefresh, filters, onAssignOrgs, currentUserRole }) => {
  const [editingUser, setEditingUser] = useState(null);
  const [assignmentCounts, setAssignmentCounts] = useState({});
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [roleList, setRoleList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => { setCurrentPage(1); }, [filters]); 

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const rolesData = await apiService.getRoles(); 
        setRoleList(rolesData);
      } catch (error) {
        console.error("Failed to fetch roles", error);
      }
    };
    fetchRoles();
  }, []);

  const fetchCounts = useCallback(async () => {
    if (!users || users.length === 0) return;
    const managers = users.filter(u => u.role === 'manager');
    if (managers.length === 0) return;
    setLoadingCounts(true);
    const newCounts = {};
    await Promise.all(managers.map(async (manager) => {
      try {
        const assignedOrgs = await apiService.getUserAssignedOrgs(manager.id);
        newCounts[manager.id] = assignedOrgs.length;
      } catch (error) {
        newCounts[manager.id] = 0;
      }
    }));
    setAssignmentCounts(prev => ({ ...prev, ...newCounts }));
    setLoadingCounts(false);
  }, [users]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  const handleEditClick = (user) => {
    setEditingUser(user);
  };

  const handleUserUpdated = () => {
    setEditingUser(null); 
    if (onRefresh) onRefresh(); 
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
  };

  const handleDeleteClick = async (userId) => {
    const isConfirmed = window.confirm("Are you sure you want to delete this user? This action cannot be undone.");
    if (!isConfirmed) return;
    try {
      await apiService.deleteUser(userId);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Delete failed", error);
      alert("Failed to delete user. Please try again.");
    }
  };

  const filteredUsers = users
    .filter(user => {
      if (!user) return false;
      const username = user.username || '';
      const email = user.email || '';
      const jobTitle = user.job_title || ''; // 1. Get the job title
      const org = user.organisation || user.organization || ''; 
      const roleKey = user.role || ''; 
      const status = user.status || '';
      const matchesSearch = filters.search === '' || 
                            username.toLowerCase().includes(filters.search.toLowerCase()) || 
                            email.toLowerCase().includes(filters.search.toLowerCase()) ||
                            jobTitle.toLowerCase().includes(filters.search.toLowerCase()); // <--- Add this line

      const matchesOrg = filters.org === '' || org === filters.org;
      const matchesRole = filters.role === '' || roleKey === filters.role;
      const matchesStatus = filters.status === '' || status === filters.status;
      return matchesSearch && matchesOrg && matchesRole && matchesStatus;
    })
    .sort((a, b) => {
      const nameA = (a.username || '').toLowerCase();
      const nameB = (b.username || '').toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentUsers = filteredUsers.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handlePrevPage = () => { if (currentPage > 1) setCurrentPage(prev => prev - 1); };
  const handleNextPage = () => { if (currentPage < totalPages) setCurrentPage(prev => prev + 1); };

  const getRoleName = (userRoleKey) => {
    if (!roleList || roleList.length === 0) return userRoleKey; 
    const foundRole = roleList.find(r => r.roleKey === userRoleKey);
    return foundRole ? foundRole.name : userRoleKey;
  };

  const renderDocVisibility = (user) => {
    if (user.role === 'admin') return <span style={{ color: '#28a745', fontWeight: 'bold' }}>All Organisations</span>;
    if (user.role === 'manager') {
      if (loadingCounts && assignmentCounts[user.id] === undefined) return <span style={{fontSize: '0.8em', color: '#999'}}>Loading...</span>;
      const count = assignmentCounts[user.id] || 0;
      const label = count === 0 ? "Home Only" : `${count} Organisation${count !== 1 ? 's' : ''}`;
      return (
        <button 
          className="btn-link-style"
          onClick={() => onAssignOrgs(user)} 
          title="Click to manage assigned organisations"
          style={{ background: 'none', border: 'none', color: '#3b82f6', textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit', padding: 0, fontWeight: '500' }}
        >
          {label}
        </button>
      );
    }
    return <span style={{ color: '#999' }}>Home Only</span>;
  };

  if (isLoading) return <div className="loading">Loading users...</div>;

  return (
    <div className="user-list-container">
      
      {editingUser && (
        <div className="modal-overlay">
          <div className="modal-content">
            <EditUserForm 
              user={editingUser} 
              organisations={organisations}
              onUserUpdated={handleUserUpdated} 
              onCancel={handleCancelEdit}
              currentUserRole={currentUserRole}
            />
          </div>
        </div>
      )}

      <div className="user-list">
        <table className="users-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Username</th>
              <th>Email</th>
              <th>Job Title</th> 
              <th>Organisation</th>
              <th>Role</th>
              <th>Doc Visibility</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.length > 0 ? (
              currentUsers.map(user => {
                // Logic to disable editing Admins if current user is System Admin
                const isTargetAdmin = user.role === 'admin';
                const canEdit = !(currentUserRole === 'system_admin' && isTargetAdmin);
                
                // CHANGE: Logic to hide Delete button for System Admin
                const canDelete = currentUserRole !== 'system_admin';

                return (
                  <tr key={user.id}>
                    <td>{user.id}</td>
                    <td><span className="username">{user.username}</span></td>
                    <td>{user.email}</td>
                    <td>{user.job_title}</td> 
                    <td>{user.organisation || user.organization}</td>
                    <td>{getRoleName(user.role)}</td>
                    <td>{renderDocVisibility(user)}</td>
                    <td>
                      <div className="user-list-action-buttons">
                        {/* Edit Button */}
                        {canEdit ? (
                          <button 
                            className="btn-edit" 
                            onClick={() => handleEditClick(user)}
                          >
                            Edit
                          </button>
                        ) : (
                          <button className="btn-edit" disabled style={{opacity: 0.5, cursor: 'not-allowed'}}>
                            Edit
                          </button>
                        )}

                        {/* Delete Button - Only show if allowed */}
                        {canDelete && (
                          <button 
                            className="btn-delete"
                            onClick={() => handleDeleteClick(user.id)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr><td colSpan="7" className="no-results">No users found matching filters.</td></tr>
            )}
          </tbody>
        </table>
        {filteredUsers.length > 0 && (
          <div className="pagination-container">
            <button className="pagination-btn" onClick={handlePrevPage} disabled={currentPage === 1}>&laquo; Previous</button>
            <span className="pagination-info">Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong></span>
            <button className="pagination-btn" onClick={handleNextPage} disabled={currentPage === totalPages}>Next &raquo;</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserList;
